import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import { setReflowGroupsAction } from 'editor/src/store/design/slice';
import { MediaText, ReflowGroup } from 'editor/src/store/design/types';
import getCurrentSpreadIndex from 'editor/src/store/editor/selector/getCurrentSpreadIndex';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import clusterIntoRows from './clusterIntoRows';
import { expandReflowRowsWithConditionSiblings } from './conditionReflowSync';
import updateHiddenElementsOperation from './updateHiddenElementsOperation';

let counter = 0;
const nextGroupId = (): string => {
  counter += 1;
  return `rg-${Date.now().toString(36)}-${counter}`;
};

/**
 * Author multi-selects 2+ text elements on the canvas and clicks
 * "Add reflow" in the Add Personalized Layer dropdown. This operation:
 *   1. Collects the currently-selected text elements on the current spread.
 *   2. Auto-clusters them into rows by y-overlap (label + value at the same y
 *      land in one row; vertically stacked items become separate rows).
 *   3. Creates a new ReflowGroup with those rows pre-populated.
 *   4. Fires updateHiddenElementsOperation to immediately compute hidden state
 *      and shift positions for any rows that start out empty.
 *
 * Returns the new group's id (or undefined if nothing to do — caller can
 * surface a toast).
 */
const createReflowGroupFromSelectionOperation =
  (defaultName: string) =>
  (dispatch: ThunkDispatch, getState: () => RootState): string | undefined => {
    const state = getState();
    const spreadIndex = getCurrentSpreadIndex(state);
    const spread = state.design.designData?.spreads[spreadIndex];
    if (!spread) {
      return undefined;
    }

    const selectedUuids = new Set(state.editor.selectedElementUuids);
    if (selectedUuids.size === 0) {
      return undefined;
    }

    const textElements: MediaText[] = [];
    spread.pages.forEach((page) => {
      page.groups.media?.forEach((element) => {
        if (element.type === 'text' && selectedUuids.has(element.uuid)) {
          textElements.push(element as MediaText);
        }
      });
    });

    if (textElements.length === 0) {
      return undefined;
    }

    const clusteredRows = clusterIntoRows(textElements);
    if (clusteredRows.length === 0) {
      return undefined;
    }

    // The canvas selection only contains the active (visible) option of any
    // condition. Pull in every sibling option's text so switching options
    // reflows correctly instead of overlapping the rows below.
    const rows = spread.conditionGroup
      ? expandReflowRowsWithConditionSiblings(clusteredRows, spread.conditionGroup)
      : clusteredRows;

    const existingGroups = spread.reflowGroups ?? [];
    const newGroup: ReflowGroup = {
      id: nextGroupId(),
      name: `${defaultName} ${existingGroups.length + 1}`,
      rows,
    };

    batch(() => {
      dispatch(
        setReflowGroupsAction({
          spreadIndex,
          reflowGroups: [...existingGroups, newGroup],
        }),
      );
      dispatch(updateHiddenElementsOperation());
    });

    return newGroup.id;
  };

export default createReflowGroupFromSelectionOperation;
